/**
 * Sound Notification Service for Dispatcher App
 * Manages audio notifications for various events
 */
import { SOUND_DEFAULTS, getSettingsSoundSettings, getSettingsSoundEnabled, getSettingsSoundVolume } from '../config/appConstants';

class SoundService {
    constructor() {
        this.enabled = this.loadSoundPreference();
        this.sounds = {};
        this._soundConfig = SOUND_DEFAULTS;
        this.initializeSounds();
    }

    /**
     * Configure sound service with settings from server.
     * Called when company settings are loaded.
     * @param {object} settings - CompanySettings object
     */
    configure(settings) {
        this._soundConfig = getSettingsSoundSettings(settings);
        const serverEnabled = getSettingsSoundEnabled(settings);
        const serverVolume = getSettingsSoundVolume(settings);

        // Server soundEnabled acts as master switch; local pref can only disable further
        if (!serverEnabled) {
            this.enabled = false;
        }
        // Update volume from settings
        this._soundConfig.defaultVolume = serverVolume;

        // Re-initialize sounds with new config
        this.initializeSounds();
    }

    /**
     * Initialize audio elements for each sound type
     */
    initializeSounds() {
        this.sounds = {
            message: this.createSound('/sounds/message.wav'),
            urgent: this.createSound('/sounds/urgent.wav'),
            sent: this.createSound('/sounds/sent.wav'),
            success: this.createSound('/sounds/success.wav'),
            broadcast: this.createSound('/sounds/broadcast.wav'),
        };
    }

    /**
     * Create an HTML Audio element that preloads a WAV file.
     * Sets _loadFailed = true if the file cannot be fetched.
     */
    createSound(url) {
        const audio = new Audio();
        audio.volume = this._soundConfig.defaultVolume ?? SOUND_DEFAULTS.defaultVolume;
        audio.src = url;
        audio.preload = 'auto';
        audio._loadFailed = false;
        audio.addEventListener('error', () => { audio._loadFailed = true; });
        return audio;
    }

    // ── Web Audio API helpers ──────────────────────────────────────────

    // Creates a fresh AudioContext for each sound.
    // A fresh context created during (or after) a user gesture is always in
    // "running" state, avoiding suspend/resume race conditions entirely.
    _getCtx() {
        return new (window.AudioContext || window.webkitAudioContext)();
    }

    // Effective volume: server setting clamped to a minimum so a misconfigured
    // soundVolume of 0 never produces silent audio.
    _vol() {
        return Math.max(0.25, this._soundConfig.defaultVolume ?? SOUND_DEFAULTS.defaultVolume);
    }

    // Bell ping — incoming message (higher pitch)
    _playBellPing() {
        const ctx = this._getCtx();
        const now = ctx.currentTime;
        const vol = this._vol();
        const dur = 0.70;
        // 2000 Hz fundamental — high, bright ping with long tail
        [2000, 4000, 6001].forEach((freq, i) => {
            const baseGains = [0.50, 0.15, 0.06];
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(baseGains[i] * vol, now);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(now);
            osc.stop(now + dur);
        });
        setTimeout(() => ctx.close().catch(() => { }), (dur + 0.3) * 1000);
    }

    // Triple ascending beep — urgent / request message (higher pitch)
    _playTripleBeep() {
        const ctx = this._getCtx();
        const now = ctx.currentTime;
        const vol = this._vol();
        const beepDur = 0.12;
        const dur = 3 * beepDur + 2 * 0.06 + 0.05;
        // Higher: 1800, 2200, 2800 Hz
        [1800, 2200, 2800].forEach((freq, i) => {
            const t = now + i * 0.18;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0, t);
            gain.gain.linearRampToValueAtTime(0.45 * vol, t + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.0001, t + beepDur);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(t);
            osc.stop(t + beepDur);
        });
        setTimeout(() => ctx.close().catch(() => { }), (dur + 0.3) * 1000);
    }

    // Mail whoosh — message sent
    _playMailWhoosh() {
        const ctx = this._getCtx();
        const now = ctx.currentTime;
        const vol = this._vol();
        const dur = 0.28;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(350, now);
        osc.frequency.exponentialRampToValueAtTime(1400, now + dur);
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.40 * vol, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + dur);
        setTimeout(() => ctx.close().catch(() => { }), (dur + 0.3) * 1000);
    }

    // Car revving then driving away — call sent
    _playCarDrivingOff() {
        const ctx = this._getCtx();
        const now = ctx.currentTime;
        const vol = this._vol();
        const dur = 3.6;    // doubled for a long fade-away

        // RPM/pitch profile shared across layers
        const applyFreq = (osc, scale = 1) => {
            osc.frequency.setValueAtTime(80 * scale, now);
            osc.frequency.linearRampToValueAtTime(200 * scale, now + 0.55);
            osc.frequency.setValueAtTime(200 * scale, now + 0.75);
            osc.frequency.linearRampToValueAtTime(100 * scale, now + dur); // gentle Doppler over full length
        };

        // Master output envelope
        const master = ctx.createGain();
        master.gain.setValueAtTime(0, now);
        master.gain.linearRampToValueAtTime(vol, now + 0.08);
        master.gain.setValueAtTime(vol, now + 0.78);
        master.gain.exponentialRampToValueAtTime(0.0001, now + dur);
        master.connect(ctx.destination);

        // ── Layer 1: AM-modulated noise — the mechanical "chug chug chug" ──
        // Noise × sawtooth(engineFreq) pulses at the firing rate — non-musical, mechanical.
        try {
            const noiseLen = Math.ceil(ctx.sampleRate * dur);
            const noiseBuf = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
            const nd = noiseBuf.getChannelData(0);
            for (let i = 0; i < noiseLen; i++) nd[i] = Math.random() * 2 - 1;
            const noiseSrc = ctx.createBufferSource();
            noiseSrc.buffer = noiseBuf;

            const bpf = ctx.createBiquadFilter();
            bpf.type = 'bandpass';
            bpf.frequency.setValueAtTime(300, now);
            bpf.frequency.linearRampToValueAtTime(1200, now + 0.55);
            bpf.frequency.linearRampToValueAtTime(400, now + dur);
            bpf.Q.value = 0.6; // looser = wider passband, less muffled

            // DC 0.5 + oscillator ±0.45 → gain swings 0.05→0.95 at engine rate
            const amGain = ctx.createGain();
            amGain.gain.setValueAtTime(0.5, now);
            const amOsc = ctx.createOscillator();
            amOsc.type = 'sawtooth'; // sawtooth = sharper "bite" per firing cycle
            applyFreq(amOsc);
            const amDepth = ctx.createGain();
            amDepth.gain.value = 0.45;
            amOsc.connect(amDepth);
            amDepth.connect(amGain.gain);
            amOsc.start(now); amOsc.stop(now + dur);

            const noiseOut = ctx.createGain();
            noiseOut.gain.value = 0.75;
            noiseSrc.connect(bpf); bpf.connect(amGain); amGain.connect(noiseOut); noiseOut.connect(master);
            noiseSrc.start(now); noiseSrc.stop(now + dur);
        } catch (_) { }

        // ── Layer 2: Lightly distorted sawtooth — pitch grunt (kept secondary) ──
        {
            const saw = ctx.createOscillator();
            saw.type = 'sawtooth';
            applyFreq(saw);
            const dist = ctx.createWaveShaper();
            const k = 20;
            const curve = new Float32Array(256);
            for (let i = 0; i < 256; i++) {
                const x = (i * 2) / 256 - 1;
                curve[i] = (Math.PI + k) * x / (Math.PI + k * Math.abs(x));
            }
            dist.curve = curve;
            dist.oversample = '2x';
            const lpf = ctx.createBiquadFilter();
            lpf.type = 'lowpass';
            lpf.frequency.setValueAtTime(800, now);
            lpf.frequency.linearRampToValueAtTime(3000, now + 0.55);
            lpf.frequency.exponentialRampToValueAtTime(600, now + dur);
            lpf.Q.value = 1;
            const sawOut = ctx.createGain();
            sawOut.gain.value = 0.22;
            saw.connect(dist); dist.connect(lpf); lpf.connect(sawOut); sawOut.connect(master);
            saw.start(now); saw.stop(now + dur);
        }

        // ── Layer 3: Backfire pops during the rev-up ──
        [0.10, 0.26, 0.42].forEach(t => {
            try {
                const popLen = Math.ceil(ctx.sampleRate * 0.045);
                const popBuf = ctx.createBuffer(1, popLen, ctx.sampleRate);
                const pd = popBuf.getChannelData(0);
                for (let i = 0; i < popLen; i++) pd[i] = Math.random() * 2 - 1;
                const popSrc = ctx.createBufferSource();
                popSrc.buffer = popBuf;
                const popLpf = ctx.createBiquadFilter();
                popLpf.type = 'lowpass';
                popLpf.frequency.value = 1200;
                const popGain = ctx.createGain();
                popGain.gain.setValueAtTime(0.28 * vol, now + t);
                popGain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.045);
                popSrc.connect(popLpf); popLpf.connect(popGain); popGain.connect(ctx.destination);
                popSrc.start(now + t); popSrc.stop(now + t + 0.045);
            } catch (_) { }
        });

        setTimeout(() => ctx.close().catch(() => { }), (dur + 0.3) * 1000);
    }

    // Double whoosh — broadcast sent (same family as message-sent whoosh, but doubled)
    _playBroadcastChime() {
        const ctx = this._getCtx();
        const now = ctx.currentTime;
        const vol = this._vol();
        const sweepDur = 0.26;
        const dur = sweepDur + 0.14 + 0.1;

        // First whoosh: identical sweep to message-sent
        const osc1 = ctx.createOscillator();
        const gain1 = ctx.createGain();
        osc1.type = 'sine';
        osc1.frequency.setValueAtTime(350, now);
        osc1.frequency.exponentialRampToValueAtTime(1400, now + sweepDur);
        gain1.gain.setValueAtTime(0, now);
        gain1.gain.linearRampToValueAtTime(0.38 * vol, now + 0.02);
        gain1.gain.exponentialRampToValueAtTime(0.0001, now + sweepDur);
        osc1.connect(gain1);
        gain1.connect(ctx.destination);
        osc1.start(now);
        osc1.stop(now + sweepDur);

        // Second whoosh: 120 ms later, slightly higher range
        const t2 = now + 0.12;
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(500, t2);
        osc2.frequency.exponentialRampToValueAtTime(2000, t2 + sweepDur);
        gain2.gain.setValueAtTime(0, t2);
        gain2.gain.linearRampToValueAtTime(0.30 * vol, t2 + 0.02);
        gain2.gain.exponentialRampToValueAtTime(0.0001, t2 + sweepDur);
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.start(t2);
        osc2.stop(t2 + sweepDur);

        setTimeout(() => ctx.close().catch(() => { }), (dur + 0.3) * 1000);
    }

    // Route to the correct synthesizer by sound type
    _playWebAudio(soundType) {
        try {
            switch (soundType) {
                case 'message': return this._playBellPing();
                case 'urgent': return this._playTripleBeep();
                case 'sent': return this._playMailWhoosh();
                case 'success': return this._playCarDrivingOff();
                case 'broadcast': return this._playBroadcastChime();
                default: break;
            }
        } catch (err) {
            console.error('❌ Web Audio error:', err);
        }
    }

    /**
     * Load sound preference from localStorage
     */
    loadSoundPreference() {
        const saved = localStorage.getItem('dispatcherSoundEnabled');
        return saved === null ? true : saved === 'true'; // Default enabled
    }

    /**
     * Save sound preference to localStorage
     */
    saveSoundPreference(enabled) {
        this.enabled = enabled;
        localStorage.setItem('dispatcherSoundEnabled', enabled.toString());
    }

    /**
     * Toggle sound on/off
     */
    toggle() {
        this.enabled = !this.enabled;
        this.saveSoundPreference(this.enabled);
        return this.enabled;
    }

    /**
     * Check if sounds are enabled
     */
    isEnabled() {
        return this.enabled;
    }

    /**
     * Play a sound by type.
     * Uses the WAV file when it has fully loaded (readyState >= 2);
     * otherwise falls back to the Web Audio API synthesizer immediately.
     */
    play(soundType) {
        if (!this.enabled) {
            return;
        }

        const sound = this.sounds[soundType];
        if (!sound) {
            console.warn('Sound type not found:', soundType);
            return;
        }

        // Only use the audio element if the file has actually loaded enough data to play.
        // readyState 2 = HAVE_CURRENT_DATA, 3 = HAVE_FUTURE_DATA, 4 = HAVE_ENOUGH_DATA.
        if (!sound._loadFailed && sound.readyState >= 2) {
            sound.currentTime = 0;
            sound.play().catch(() => this._playWebAudio(soundType));
        } else {
            this._playWebAudio(soundType);
        }
    }

    /**
     * Play sound when a new message arrives
     * Checks message content to determine if it's urgent
     */
    playMessageSound(messageText) {
        if (!this.enabled) {
            return;
        }


        // Check if message contains urgent keywords
        const text = messageText || '';
        const urgentKeywords = this._soundConfig.urgentKeywords || SOUND_DEFAULTS.urgentKeywords;
        const isUrgent = urgentKeywords.some(keyword =>
            text.toLowerCase().includes(keyword)
        );

        this.play(isUrgent ? 'urgent' : 'message');
    }

    /**
     * Play sound when a message is sent
     */
    playMessageSentSound() {
        this.play('sent');
    }

    /**
     * Play sound when a call is created/sent
     */
    playCallSentSound() {
        this.play('success');
    }

    /**
     * Play sound when a broadcast message is sent
     */
    playBroadcastSentSound() {
        this.play('broadcast');
    }

    /**
     * Test method — plays all synthesized sounds in sequence.
     * Bypasses the enabled flag and WAV files so it always produces audio.
     * Call from browser console: window.testSounds()
     */
    testAllSounds() {
        const types = ['message', 'urgent', 'sent', 'success', 'broadcast'];
        types.forEach((type, i) => {
            setTimeout(() => {
                console.log(`🔊 Playing sound: ${type}`);
                this._playWebAudio(type);
            }, i * 1700);
        });
    }
}

// Export singleton instance
const soundService = new SoundService();

// Make test function available globally
if (typeof window !== 'undefined') {
    window.testSounds = () => soundService.testAllSounds();
    window.soundService = soundService;
}

export default soundService;
